import produce from 'immer';
import { uniqueId } from 'lodash';
import type { ApolloCache } from '@apollo/client';
import { mergeDeep } from '@apollo/client/utilities';
import { useApolloClient } from '@frameio/graphql-client';
import type { WatermarkTemplateInput } from '@frameio/graphql-client/generated';
import {
  useActiveAccountId,
  useActiveProjectId,
  useActiveWorkspaceId,
} from '@frameio/shared/contexts';
import type { CacheUpdater } from '../../utils/types';
import {
  AccountWatermarkTemplates,
  ProjectWatermarkTemplates,
  useAccountWatermarkTemplatesFragment,
  useHasSessionBasedTemplatesFragment,
  useProjectWatermarkTemplatesFragment,
  useWorkspaceWatermarkTemplatesFragment,
  WatermarkTemplateComplete,
  WorkspaceWatermarkTemplates,
} from '../gql/fragments/generated/watermark_templates';
import type {
  AddWatermarkTemplateMutation,
  AddWatermarkTemplateMutationVariables,
} from '../gql/mutations/generated/AddWatermarkTemplate';
import { useAddWatermarkTemplateMutation } from '../gql/mutations/generated/AddWatermarkTemplate';
import { useDeleteWatermarkTemplateMutation } from '../gql/mutations/generated/DeleteWatermarkTemplate';
import type {
  UpdateWatermarkTemplateMutation,
  UpdateWatermarkTemplateMutationVariables,
} from '../gql/mutations/generated/UpdateWatermarkTemplate';
import { useUpdateWatermarkTemplateMutation } from '../gql/mutations/generated/UpdateWatermarkTemplate';
import { useGetAccountWatermarkTemplatesLazyQuery } from '../gql/queries/generated/GetAccountWatermarkTemplates';
import { useGetProjectWatermarkTemplatesLazyQuery } from '../gql/queries/generated/GetProjectWatermarkTemplates';
import { useGetWorkspaceWatermarkTemplatesLazyQuery } from '../gql/queries/generated/GetWorkspaceWatermarkTemplates';
import { getTemplate } from '../utils';

type WatermarkTemplateScope = 'workspace' | 'project' | 'account';

/**
 * CRUD operations for managing watermark templates at the account, workspace, or project level.
 *
 * Technically, templates only exist on the account level (currently). But since not all users have account-level permissions,
 * we can access the same templates but through the workspace- or project-level. Since there are different permission checks
 * depending on where the templates are being fetched, we need to store the data separately in cache.
 *
 * NOTE: This hook's behavior can change depending on where it is called (i.e. if it has access to a workspaceId or projectId context).
 * Project routes default to project scope. Outside a project route, workspace scope wins over account scope.
 */
export const useWatermarkTemplates = () => {
  const client = useApolloClient();
  const accountId = useActiveAccountId();
  const projectId = useActiveProjectId();
  const workspaceId = useActiveWorkspaceId() ?? '';

  let scope: WatermarkTemplateScope = 'account';
  if (workspaceId) scope = 'workspace';
  if (projectId) scope = 'project';

  const [addTemplate] = useAddWatermarkTemplateMutation();
  const [updateTemplate] = useUpdateWatermarkTemplateMutation();
  const [deleteTemplate] = useDeleteWatermarkTemplateMutation();

  const [fetchAccountTemplates, { loading: isLoadingAccountTemplates }] =
    useGetAccountWatermarkTemplatesLazyQuery({
      variables: { accountId },
    });

  const [fetchProjectTemplates, { loading: isLoadingProjectTemplates }] =
    useGetProjectWatermarkTemplatesLazyQuery({
      variables: { projectId },
    });

  const [fetchWorkspaceTemplates, { loading: isLoadingWorkspaceTemplates }] =
    useGetWorkspaceWatermarkTemplatesLazyQuery({
      variables: { workspaceId },
    });

  const isLoading =
    isLoadingAccountTemplates ||
    isLoadingProjectTemplates ||
    isLoadingWorkspaceTemplates;

  const accountFragment = useAccountWatermarkTemplatesFragment(accountId);
  const projectFragment = useProjectWatermarkTemplatesFragment(projectId);
  const workspaceFragment = useWorkspaceWatermarkTemplatesFragment(workspaceId);
  const hasTemplatesFragment = useHasSessionBasedTemplatesFragment(accountId);

  const fragmentByScope = {
    workspace: workspaceFragment,
    project: projectFragment,
    account: accountFragment,
  };
  const fetchByScope = {
    workspace: fetchWorkspaceTemplates,
    project: fetchProjectTemplates,
    account: fetchAccountTemplates,
  };
  const identityByScope = {
    workspace: { __typename: 'Workspace', id: workspaceId },
    project: { __typename: 'Project', id: projectId },
    account: { __typename: 'Account', id: accountId },
  };
  const cacheFragmentByScope = {
    workspace: [WorkspaceWatermarkTemplates, 'WorkspaceWatermarkTemplates'],
    project: [ProjectWatermarkTemplates, 'ProjectWatermarkTemplates'],
    account: [AccountWatermarkTemplates, 'AccountWatermarkTemplates'],
  } as const;

  const templateFragment = fragmentByScope[scope];

  const { watermarkTemplates: templateConnection } = templateFragment.complete
    ? templateFragment.data
    : {};

  const hasSessionBasedTemplates = Boolean(
    hasTemplatesFragment.data.hasSessionBasedTemplates
  );

  const handleFetchTemplates = fetchByScope[scope];

  const identity = identityByScope[scope];

  const cacheFragment = cacheFragmentByScope[scope];
  const workspaceContextInput = workspaceId ? { workspaceId } : undefined;

  const handleAddTemplate = (watermarkTemplate: WatermarkTemplateInput) => {
    const variables = {
      input: { accountId, ...workspaceContextInput, watermarkTemplate },
    };

    const optimisticResponse = {
      createWatermarkTemplate: {
        watermarkTemplate: {
          __typename: 'WatermarkTemplate' as const,
          id: uniqueId('optimistic-template-'),
          name: watermarkTemplate.name ?? '',
          deletedAt: null,
          inUse: false,
          blocks: [],
        },
      },
    };

    const update: CacheUpdater<
      AddWatermarkTemplateMutation,
      AddWatermarkTemplateMutationVariables
    > = (cache, { data }) => {
      const newTemplate = data?.createWatermarkTemplate?.watermarkTemplate;
      if (!newTemplate) return;

      const [fragment, fragmentName] = cacheFragment;

      cache.updateFragment(
        { id: cache.identify(identity), fragment, fragmentName },
        (connection) => {
          return produce(connection, (draft) => {
            if (!draft?.watermarkTemplates) return;
            draft.watermarkTemplates.nodes.unshift(newTemplate);
            draft.watermarkTemplates.totalCount += 1;
          });
        }
      );
    };

    return addTemplate({ optimisticResponse, update, variables });
  };

  const handleUpdateTemplate = (
    watermarkTemplateId: string,
    watermarkTemplate: WatermarkTemplateInput
  ) => {
    const currentTemplate = getTemplate(client.cache, watermarkTemplateId);
    if (!currentTemplate) return undefined;

    const variables = {
      input: {
        watermarkTemplateId,
        ...workspaceContextInput,
        watermarkTemplate,
      },
    };

    const optimisticResponse = {
      updateWatermarkTemplate: {
        watermarkTemplate: mergeDeep(currentTemplate, watermarkTemplate),
      },
    };

    const update: CacheUpdater<
      UpdateWatermarkTemplateMutation,
      UpdateWatermarkTemplateMutationVariables
    > = (cache, { data }) => {
      const nextTemplate = data?.updateWatermarkTemplate?.watermarkTemplate;
      if (!nextTemplate) return;

      cache.updateFragment(
        {
          id: cache.identify({
            __typename: 'WatermarkTemplate',
            id: watermarkTemplateId,
          }),
          fragment: WatermarkTemplateComplete,
        },
        (previousData) => mergeDeep(previousData, nextTemplate)
      );
    };

    return updateTemplate({ optimisticResponse, update, variables });
  };

  const handleDeleteTemplate = (templateId: string) => {
    const variables = {
      input: { watermarkTemplateId: templateId, ...workspaceContextInput },
    };

    const optimisticResponse = {
      deleteWatermarkTemplate: {
        watermarkTemplate: { id: templateId },
      },
    };

    const update = (cache: ApolloCache<any>) => {
      const [fragment, fragmentName] = cacheFragment;

      cache.updateFragment(
        { id: cache.identify(identity), fragment, fragmentName },
        (connection) => {
          return produce(connection, (draft) => {
            if (!draft?.watermarkTemplates) return;
            const index = draft.watermarkTemplates.nodes.findIndex(
              (node) => node.id === templateId
            );
            if (index === -1) return;
            draft.watermarkTemplates.nodes.splice(index, 1);

            const totalCount = draft.watermarkTemplates.totalCount;
            draft.watermarkTemplates.totalCount = Math.max(totalCount - 1, 0);
          });
        }
      );

      cache.evict({
        id: cache.identify({ __typename: 'WatermarkTemplate', id: templateId }),
      });
    };

    return deleteTemplate({ optimisticResponse, update, variables });
  };

  return {
    add: handleAddTemplate,
    connection: templateConnection,
    delete: handleDeleteTemplate,
    fetch: handleFetchTemplates,
    hasSessionBasedTemplates,
    loading: isLoading,
    update: handleUpdateTemplate,
  };
};
